/**
 * #580 F-08 slice 4a3 (design I9, I11) — changing payday, the client half.
 *
 * The server (4a2) pays everything owed under the OLD setting and then changes it, in one
 * transaction: `GET /api/payday/change-preview`, `POST /api/payday/change`,
 * `GET /api/payday/changes/:operationKey`. This module makes those three calls and checks what
 * comes back. It never decides what to SAY; `components/PaydayChangePanel.jsx` does.
 *
 * **A 200 is not a payment.** A committed answer is believed only when it names our key, our
 * target, the old setting, the currency, the total and every child's money exactly as the preview
 * the parent confirmed. Anything else is an unknown outcome.
 *
 * **The request is written down BEFORE it is sent** (`writePending`), scoped to the family and the
 * parent, so a lost answer, a refresh or a closed tab can check the ORIGINAL change instead of
 * starting a new one. Only `client.js` is imported, never `session.js`: the route tests mock that
 * module whole.
 */
import { ApiError, api } from './client.js';
import { PAYDAY_PERIODS } from '../lib/payday.js';

export const PREVIEW_PATH = '/api/payday/change-preview';
export const CHANGE_PATH = '/api/payday/change';
export const changePath = (operationKey) => `/api/payday/changes/${encodeURIComponent(operationKey)}`;

/** The one sign that the rollout row is off: the three routes refuse to act at all. */
export const NOT_ENABLED = 'PAYDAY_CHANGE_NOT_ENABLED';
/** The old PATCH's refusal of a real payday change while the rollout is on. */
export const CONFIRMATION_REQUIRED = 'PAYDAY_CONFIRMATION_REQUIRED';

export const codeOf = (error) => (error instanceof ApiError && error.body ? error.body.code : undefined);

const cents = (value) => {
  if (!['string', 'number'].includes(typeof value) || !/^\d+(?:\.\d{1,2})?$/.test(String(value))) return NaN;
  const result = Math.round(Number(value) * 100);
  return Number.isSafeInteger(result) ? result : NaN;
};
const isText = (v) => typeof v === 'string' && v !== '';
const jarCents = (jars) => ['save', 'give', 'spend'].map((key) => cents(jars?.[key]));
function toBatch(batch) {
  if (!batch || !isText(batch.periodStart) || !isText(batch.periodEnd)
    || !(Date.parse(batch.periodEnd) > Date.parse(batch.periodStart))) return null;
  const totalCents = cents(batch.total);
  const jars = jarCents(batch.jars);
  if (!Number.isSafeInteger(totalCents) || !jars.every(Number.isSafeInteger)
    || jars.reduce((sum, n) => sum + n, 0) !== totalCents) return null;
  return { periodStart: batch.periodStart, periodEnd: batch.periodEnd, totalCents, jarCents: jars };
}
const batchSignature = (b) => JSON.stringify([Date.parse(b.periodStart), Date.parse(b.periodEnd), b.totalCents, b.jarCents]);

// A definitive rollout answer lasts for this page session, scoped to both family and parent.
const modes = new Map();
export function rememberChangeMode(tenantId, actorId, mode) {
  if (tenantId && actorId != null && ['on', 'off'].includes(mode)) modes.set(pendingKey(tenantId, actorId), mode);
}
/** Clear page-session knowledge (also isolates independent test sessions). */
export function clearChangeModeCache() { modes.clear(); }


/** A preview this screen can show and confirm, or `null`. Every figure it will state is checked. */
export function toPreview(body, target) {
  if (!body || typeof body !== 'object') return null;
  const from = body.oldSetting && body.oldSetting.paydayPeriod;
  if (!PAYDAY_PERIODS.includes(target) || !PAYDAY_PERIODS.includes(from) || !body.newSetting || body.newSetting.paydayPeriod !== target) return null;
  if (typeof body.noChange !== 'boolean' || !isText(body.previewToken) || !isText(body.currency)) return null;
  if (!Array.isArray(body.recipients) || !Array.isArray(body.repairRequired)) return null;
  if (!Number.isInteger(body.pendingReviewCount) || body.pendingReviewCount < 0) return null;
  const recipients = [];
  const ids = new Set();
  for (const r of body.recipients) {
    if (!r || !Number.isSafeInteger(r.userId) || r.userId <= 0 || ids.has(r.userId) || !Number.isFinite(cents(r.total)) || !r.jars) return null;
    ids.add(r.userId);
    const jars = ['save', 'give', 'spend'].map((k) => cents(r.jars[k]));
    if (!jars.every(Number.isFinite) || jars[0] + jars[1] + jars[2] !== cents(r.total)) return null;
    if (!Array.isArray(r.batches) || !r.batches.length) return null;
    const batches = r.batches.map(toBatch);
    if (batches.some((b) => !b) || batches.reduce((sum, b) => sum + b.totalCents, 0) !== cents(r.total)
      || jars.some((n, i) => n !== batches.reduce((sum, b) => sum + b.jarCents[i], 0))) return null;
    recipients.push({ userId: r.userId, name: isText(r.firstName) ? r.firstName : 'A child', totalCents: cents(r.total), jarCents: jars, batches });
  }
  if (recipients.length) {
    if (!isText(body.timezone)) return null;
    try { new Intl.DateTimeFormat('en-US', { timeZone: body.timezone }); } catch { return null; }
  }
  const totalCents = cents(body.total);
  if (!Number.isFinite(totalCents) || totalCents !== recipients.reduce((sum, r) => sum + r.totalCents, 0)) return null;
  return {
    from, to: target, noChange: body.noChange, previewToken: body.previewToken, currency: body.currency,
    expiresAt: body.expiresAt, timezone: body.timezone, recipients, totalCents, pendingReviewCount: body.pendingReviewCount,
    repairRequired: body.repairRequired,
  };
}

/** @throws {ApiError} as the server refused, or status 200 when the answer cannot be trusted */
export async function fetchPreview(target) {
  const body = await api.get(`${PREVIEW_PATH}?paydayPeriod=${encodeURIComponent(target)}`);
  const preview = toPreview(body, target);
  if (!preview) throw new ApiError('the payday change preview could not be read', { status: 200, path: PREVIEW_PATH, body });
  return preview;
}

/**
 * Is the change flow switched on? One no-change preview of the SAVED value.
 *
 * It writes nothing, but it is NOT a plain read: the server takes the household payday lock
 * (`paydayLock.js`, shared with every financial writer, 10 s lock timeout) and the parent's row
 * before it answers. The first uncached parent screen load can wait behind, and briefly hold up,
 * that family's money writes; definitive answers are reused for this family and parent page session. A 403, a 429 from a rate limiter, a 503 or a 404 from an older server all answer
 * `unknown`, which behaves as `off`.
 * @returns {Promise<'on'|'off'|'unknown'>} `unknown` behaves as `off`: today's screen, today's PATCH.
 */
export async function probeChangeMode(savedPeriod, tenantId, actorId) {
  const scope = pendingKey(tenantId, actorId);
  if (modes.has(scope)) return modes.get(scope);
  let mode;
  try {
    await fetchPreview(savedPeriod);
    mode = 'on';
  } catch (error) {
    mode = codeOf(error) === NOT_ENABLED ? 'off' : 'unknown';
  }
  rememberChangeMode(tenantId, actorId, mode);
  return mode;
}

/** The same key and body every time this request is sent, first try or retry. */
export const sendChange = (pending) => api.post(CHANGE_PATH, {
  paydayPeriod: pending.to, previewToken: pending.previewToken, operationKey: pending.operationKey,
});

export const readChange = (operationKey) => api.get(changePath(operationKey));

/**
 * Does this committed answer describe EXACTLY the change the parent confirmed?
 * Recipients are compared as a whole: the same children, each paid the previewed total.
 */
export function receiptMatches(answer, pending) {
  if (!answer || answer.status !== 'committed' || answer.operationKey !== pending.operationKey) return false;
  if (!answer.newSetting || answer.newSetting.paydayPeriod !== pending.to) return false;
  if (!answer.oldSetting || answer.oldSetting.paydayPeriod !== pending.from) return false;
  if (answer.currency !== pending.currency || cents(answer.total) !== pending.totalCents) return false;
  if (!Array.isArray(answer.receipts)) return false;
  if (!Array.isArray(pending.recipients)) return false;
  const paid = new Map();
  const payoutIds = new Set();
  for (const r of answer.receipts) {
    if (!r || !Number.isSafeInteger(r.payoutId) || r.payoutId <= 0 || payoutIds.has(r.payoutId)
      || !Number.isSafeInteger(r.userId) || !isText(r.settledAt) || !Number.isFinite(Date.parse(r.settledAt))) return false;
    payoutIds.add(r.payoutId);
    const batch = toBatch(r);
    if (!batch) return false;
    const batches = paid.get(r.userId) || [];
    batches.push(batch);
    paid.set(r.userId, batches);
  }
  if (paid.size !== pending.recipients.length) return false;
  return pending.recipients.every((r) => {
    const batches = paid.get(r.userId);
    if (!batches || batches.reduce((sum, b) => sum + b.totalCents, 0) !== r.totalCents) return false;
    if (r.jarCents && r.jarCents.some((n, i) => n !== batches.reduce((sum, b) => sum + b.jarCents[i], 0))) return false;
    // Older persisted requests kept only recipient totals. New requests bind every batch too.
    if (!r.batches) return true;
    const expected = r.batches.map(batchSignature).sort();
    return JSON.stringify(batches.map(batchSignature).sort()) === JSON.stringify(expected);
  });
}

/**
 * The server's DEFINITIVE no-write answer: the family already has the target setting (another
 * parent set it first), so nothing was paid and no operation was stored. Two shapes are accepted,
 * with and without `operationKey` and `currency`; when present they must be ours.
 */
export function isUnchanged(answer, pending) {
  if (!answer || answer.status !== 'unchanged' || !answer.newSetting || answer.newSetting.paydayPeriod !== pending.to) return false;
  if (answer.operationKey !== undefined && answer.operationKey !== pending.operationKey) return false;
  if (answer.currency !== undefined && answer.currency !== pending.currency) return false;
  return Array.isArray(answer.receipts) && answer.receipts.length === 0 && cents(answer.total) === 0;
}

/** A fresh caller-held key for ONE confirmation. Reused, never replaced, while its outcome is unknown. */
export function newChangeKey() {
  const random = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  return `payday-change-${random}`;
}

export const PENDING_KEY_PREFIX = 'chorestory.paydayChange';

/** One family AND one parent: another family, or another parent after a logout, never sees it. */
export function pendingKey(tenantId, actorId) {
  if (!isText(tenantId) || actorId === null || actorId === undefined || String(actorId) === '') return null;
  return `${PENDING_KEY_PREFIX}.${tenantId}.${actorId}`;
}

/** The unresolved request, or `null`. localStorage, so a closed tab can still check its change. */
export function readPending(tenantId, actorId) {
  const key = pendingKey(tenantId, actorId);
  if (!key) return null;
  try {
    const record = JSON.parse(window.localStorage.getItem(key) || 'null');
    return record && isText(record.operationKey) && isText(record.to) && isText(record.previewToken) ? record : null;
  } catch {
    return null;
  }
}

export function writePending(tenantId, actorId, record) {
  const key = pendingKey(tenantId, actorId);
  if (!key) return false;
  try {
    const encoded = JSON.stringify(record);
    window.localStorage.setItem(key, encoded);
    return window.localStorage.getItem(key) === encoded;
  } catch {
    return false;
  }
}

export function clearPending(tenantId, actorId) {
  const key = pendingKey(tenantId, actorId);
  if (!key) return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* Storage refused. The record stays, and it only offers a check that the server answers. */
  }
}
